import { forwardRef, ReactNode, useState } from "react";
import { Align } from "@radix-ui/react-popper";
import { ButtonProps } from "../button";
import { ButtonExpandable } from "../button-expandable/button-expandable";
import { Dropdown } from "../../dropdown";

export type DropDownItemValue = string | number;

export type DropDownItem = {
  /** Value of the item. */
  value: DropDownItemValue;
  /** Label to display for the item. Defaults to item value. */
  label?: string;
  /** Icon to be displayed on the left side of the item. */
  icon?: ReactNode;
};

export interface ButtonDropdownProps extends ButtonProps {
  /** These are the items that will display once the dropdown is opened. */
  items: DropDownItem[];
  /** This is the method that will be given to each dropdown item. This method will be called when an item is clicked. */
  onItemClick?: (dropDownItemValue: DropDownItemValue) => void;
  collapsedChevron?: ReactNode;
  expandedChevron?: ReactNode;
  /** Defines the alignment of the dropdown content with respect to chevron */
  dropDownContentAlignment?: Align;
}

export const ButtonDropdown = forwardRef<
  HTMLButtonElement,
  ButtonDropdownProps
>((props, ref) => {
  const {
    items = [],
    variant = "tertiary-black",
    onItemClick,
    children,
    collapsedChevron,
    expandedChevron,
    dropDownContentAlignment = "end",
    ...rest
  } = props;

  const [isOpen, setOpen] = useState<boolean>(false);

  const handleDropDownItemClick = (item: DropDownItem) => {
    if (onItemClick) {
      onItemClick(item.value);
    }
  };

  const dropDownItemsToDisplay = items.map((item, index) => {
    return (
      <Dropdown.Item key={index} onClick={() => handleDropDownItemClick(item)}>
        {item.icon}
        {item.label ?? item.value}
      </Dropdown.Item>
    );
  });

  return (
    <Dropdown
      modal={false}
      open={isOpen}
      onOpenChange={setOpen}
      align={dropDownContentAlignment}
      disabled={rest.disabled}
      trigger={
        <ButtonExpandable
          variant={variant}
          expanded={isOpen}
          collapsedChevron={collapsedChevron}
          expandedChevron={expandedChevron}
          {...rest}
        >
          {children}
        </ButtonExpandable>
      }
    >
      {dropDownItemsToDisplay}
    </Dropdown>
  );
});

ButtonDropdown.displayName = "ButtonDropdown";
